"use client";

import Grid from "@afa-shared/afa-components/dist/Grid";
import Heading from "@afa-shared/afa-components/dist/Heading";
import AccordionDetails, { type AccordionItem } from "@components/Accordion";
import FilterDropdowns from "@components/Filters/FilterDropdowns";
import RichTextRenderer from "@components/RichTextRenderer";
import CFContentArea from "@components/cFContentArea";
import { filterFaqMeta, getFaqFilterHeading, getFaqMetaFilterOptions } from "@lib/faqFilters";
import { useQueryState } from "nuqs";
import React, { useMemo } from "react";

import { Context } from "types/context/Context";
import { FaqMeta } from "types/faqFilters";

import faqListStyles from "../../components/FAQList/faqList.module.css";

interface FaqFilteredContentProps {
  faqMeta: ReadonlyArray<FaqMeta>;
  answerDataMap: Record<string, { json: unknown; links: unknown }>;
  context: Context;
  mainAreaItems: ReadonlyArray<unknown>;
  mainBodyBottom?: { json?: unknown; links?: unknown } | null;
  mainAreaBottomItems?: ReadonlyArray<unknown> | null;
  contentfulEntryId: string;
  maxWidth: number;
}

export default function FaqFilteredContent({
  faqMeta,
  answerDataMap,
  context,
  mainAreaItems,
  mainBodyBottom,
  mainAreaBottomItems,
  contentfulEntryId,
  maxWidth,
}: FaqFilteredContentProps) {
  const [insurance] = useQueryState("insurance", { defaultValue: "" });
  const [subject] = useQueryState("subject", { defaultValue: "" });

  const filterState = useMemo(
    () => ({
      insuranceEvent: insurance || undefined,
      subject: subject || undefined,
    }),
    [insurance, subject],
  );

  const hasActiveFilter = Boolean(filterState.insuranceEvent || filterState.subject);

  const faqFilterOptions = useMemo(
    () => getFaqMetaFilterOptions([...faqMeta], filterState),
    [faqMeta, filterState],
  );

  const filteredMeta = useMemo(
    () => (hasActiveFilter ? filterFaqMeta(faqMeta, filterState) : []),
    [hasActiveFilter, faqMeta, filterState],
  );

  const filterHeading = hasActiveFilter ? getFaqFilterHeading(filterState) : undefined;
  const filteredFaqSectionTitle = filterHeading ?? "Filtrerade frågor och svar";
  const visibleResultCount = hasActiveFilter ? filteredMeta.length : faqMeta.length;
  const resultCountLabel = `${visibleResultCount} ${visibleResultCount === 1 ? "träff" : "träffar"}`;
  const filterHeadingPrefix = filterHeading ? `${filterHeading}. ` : "";
  const statusAnnouncement = hasActiveFilter
    ? `${filterHeadingPrefix}${resultCountLabel}`
    : resultCountLabel;

  const filteredAccordionItems: AccordionItem[] = useMemo(
    () =>
      filteredMeta.map((meta, i) => {
        const data = answerDataMap[meta.id];
        return {
          id: `faq-filtered-${meta.id}-${i}`,
          question: meta.question,
          expanded: false,
          answer: data ? (
            <RichTextRenderer json={data.json} links={data.links} context={context} />
          ) : null,
        };
      }),
    [filteredMeta, answerDataMap, context],
  );

  return (
    <>
      <FilterDropdowns
        insuranceOptions={faqFilterOptions.insuranceOptions}
        subjectOptions={faqFilterOptions.subjectOptions}
      />

      <p className={faqListStyles.screenReaderWrapper} aria-live="polite" role="status">
        {statusAnnouncement}
      </p>

      {hasActiveFilter && (
        <div className={faqListStyles.resultSummary}>
          {filterHeading && (
            <Heading variant="h2" removeMargin>
              {filterHeading}
            </Heading>
          )}
          <p className={faqListStyles.resultCount}>{resultCountLabel}</p>
        </div>
      )}

      {hasActiveFilter && filteredMeta.length > 0 ? (
        <div className={faqListStyles.faqListBlock}>
          <Grid data-blockname="faq" className={faqListStyles.faqList}>
            <AccordionDetails
              variant="faq"
              items={filteredAccordionItems}
              headingTag="h3"
              tracking={{
                event: "faq_click",
                insurance: filterState.insuranceEvent || context?.insuranceName,
                sectionTitle: filteredFaqSectionTitle,
                sectionIndex: 1,
                subject: filterState.subject,
              }}
            />
          </Grid>
        </div>
      ) : (
        <>
          <CFContentArea
            desktopColumns={1}
            mobileColumns={1}
            tabletColumns={1}
            contentAreaItems={mainAreaItems}
            maxWidth={maxWidth}
            context={context}
          />

          {mainBodyBottom?.json && (
            <div
              data-contentful-entry-id={contentfulEntryId}
              data-contentful-field-id="mainBodyBottom"
            >
              <RichTextRenderer
                json={mainBodyBottom.json}
                links={mainBodyBottom.links}
                pageTextArea
              />
            </div>
          )}

          {mainAreaBottomItems && mainAreaBottomItems.length > 0 && (
            <CFContentArea
              desktopColumns={1}
              mobileColumns={1}
              tabletColumns={1}
              contentAreaItems={mainAreaBottomItems}
              maxWidth={maxWidth}
              context={context}
            />
          )}
        </>
      )}
    </>
  );
}
